import type { QueryClient } from '@tanstack/react-query'
import {
  createMemoryHistory,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'

import { ErrorPanel } from '@/app/ErrorPanel'
import { resolveNotificationTarget } from './externalModules/notificationTarget'
import { LoginPage } from '@/pages/LoginPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { validateTaskSearch } from '@/shared/routing/taskSearch'
import { validateEncounterSearch } from '@/shared/routing/encounterSection'
import { safeReturnTarget } from '@/shared/routing/returnTarget'
import { externalAuthorizationSearch } from '@/shared/routing/externalAuthorization'
import { designProposalSearch } from '@/shared/routing/designProposalSearch'
import { validateModuleEventSearch } from '@/shared/routing/moduleEventSearch'
import { validateModuleWorkspaceSearch } from '@/shared/routing/moduleWorkspaceSearch'
import { validatePatientSearch } from '@/shared/routing/patientOrigin'
import { validatePatientRegistrationSearch } from '@/shared/routing/patientRegistration'
import { validateScheduleSearch } from '@/shared/schedule/day'
import {
  FINANCE_NAV_ITEMS,
  FINANCE_SECTION_CAPABILITIES,
} from '@/shared/finance/financeNavigation'
import {
  CAPABILITIES,
  FINANCE_AREA_CAPABILITIES,
  LEADS_AREA_CAPABILITIES,
  SCHEDULE_AREA_CAPABILITIES,
} from '@/shared/session/capabilities'
import { WORKSPACE_DESTINATIONS } from '@/shared/workspace/workspaceDestinations'

import { createSessionRoutes } from './RouteLayout'
import {
  enterControlCenter, enterFinance, enterSchedule, requireAnyCapability, requireCapability,
  requireControlCenterModule, requireControlCenterSection, requireDesignDrafts, requireProfileSection, requireControlCenterUserCard, requireControlCenterView,
  requireProcessing, requireScheduleSection, requireTrackerReports, enterPlatform, requirePlatformSection, requirePlatformView, requireImagingImports,
} from './routeAccess'

export type { RouterContext } from './routeAccess'

// Guards and route metadata stay eager; the router preloads screens alongside guards.
// Login, not-found and error screens must render even when chunk loading fails.
const AppShell = lazyRouteComponent(() => import('@/app/AppShell'), 'AppShell')
const ExternalEntryPage = lazyRouteComponent(() => import('@/pages/ExternalEntryPage'), 'ExternalEntryPage')
const ClinicalRecordPage = lazyRouteComponent(
  () => import('@/pages/ClinicalRecordPage'),
  'ClinicalRecordPage',
)
const ControlCenterPage = lazyRouteComponent(
  () => import('@/pages/ControlCenterPage'),
  'ControlCenterPage',
)
const DashboardPage = lazyRouteComponent(() => import('@/pages/DashboardPage'), 'DashboardPage')
const ImportsPage = lazyRouteComponent(() => import('@/pages/AdministrationWorkPages'), 'ImportsPage')
const UpdatesPage = lazyRouteComponent(() => import('@/pages/AdministrationWorkPages'), 'UpdatesPage')
const HelpPage = lazyRouteComponent(() => import('@/pages/HelpPage'), 'HelpPage')
const EncounterPage = lazyRouteComponent(() => import('@/pages/EncounterPage'), 'EncounterPage')
const LeadsPage = lazyRouteComponent(() => import('@/pages/LeadsPage'), 'LeadsPage')
const ReferralPartnersPage = lazyRouteComponent(
  () => import('@/pages/ReferralPartnersPage'),
  'ReferralPartnersPage',
)
const FinanceAccountsPage = lazyRouteComponent(
  () => import('@/pages/finance/FinanceAccountsPage'),
  'FinanceAccountsPage',
)
const FinanceAccountPage = lazyRouteComponent(
  () => import('@/pages/finance/FinanceAccountPage'),
  'FinanceAccountPage',
)
const FinancePeoplePage = lazyRouteComponent(
  () => import('@/pages/finance/FinancePeoplePage'),
  'FinancePeoplePage',
)
const FinancePrepaidPage = lazyRouteComponent(
  () => import('@/pages/finance/FinancePrepaidPage'),
  'FinancePrepaidPage',
)
const FinanceAdministrationPage = lazyRouteComponent(
  () => import('@/pages/finance/FinanceAdministrationPage'),
  'FinanceAdministrationPage',
)
const ModulePage = lazyRouteComponent(() => import('@/pages/ModulePage'), 'ModulePage')
const ModuleEventPage = lazyRouteComponent(
  () => import('@/pages/ModuleEventPage'),
  'ModuleEventPage',
)
const ModuleReconciliationPage = lazyRouteComponent(
  () => import('@/pages/ModuleReconciliationPage'),
  'ModuleReconciliationPage',
)
const PatientOverviewPage = lazyRouteComponent(
  () => import('@/pages/PatientOverviewPage'),
  'PatientOverviewPage',
)
const PatientsPage = lazyRouteComponent(() => import('@/pages/PatientsPage'), 'PatientsPage')
const FinanceServicesPage = lazyRouteComponent(
  () => import('@/pages/finance/FinanceServicesPage'),
  'FinanceServicesPage',
)
const FinancePriceListsPage = lazyRouteComponent(
  () => import('@/pages/finance/FinancePriceListsPage'),
  'FinancePriceListsPage',
)
const FinancePriceListPage = lazyRouteComponent(
  () => import('@/pages/finance/FinancePriceListPage'),
  'FinancePriceListPage',
)
const FinanceTaxesPage = lazyRouteComponent(() => import('@/pages/finance/FinanceTaxesPage'), 'FinanceTaxesPage')
const FinanceExternalCostsPage = lazyRouteComponent(
  () => import('@/pages/finance/FinanceExternalCostsPage'),
  'FinanceExternalCostsPage',
)
const SchedulePage = lazyRouteComponent(() => import('@/pages/SchedulePage'), 'SchedulePage')
const ServiceRequestPage = lazyRouteComponent(
  () => import('@/pages/ServiceRequestPage'),
  'ServiceRequestPage',
)
const TasksPage = lazyRouteComponent(() => import('@/pages/TasksPage'), 'TasksPage')
const ProcessingRegistryPage = lazyRouteComponent(
  () => import('@/pages/processing/ProcessingRegistryPage'),
  'ProcessingRegistryPage',
)
const ProcessingPage = lazyRouteComponent(() => import('@/pages/processing/ProcessingPage'), 'ProcessingPage')
const ExternalEntryProposalPage = lazyRouteComponent(
  () => import('@/pages/processing/externalEntries/ExternalEntryProposalPage'),
  'ExternalEntryProposalPage',
)
const TrackerReportsPage = lazyRouteComponent(
  () => import('@/pages/TrackerReportsPage'),
  'TrackerReportsPage',
)
const TrackerReportPage = lazyRouteComponent(
  () => import('@/pages/TrackerReportPage'),
  'TrackerReportPage',
)
const UserProfilePage = lazyRouteComponent(
  () => import('@/pages/UserProfilePage'),
  'UserProfilePage',
)
const RequiredPasswordChangePage = lazyRouteComponent(
  () => import('@/pages/RequiredPasswordChangePage'),
  'RequiredPasswordChangePage',
)
const PublicPatientDocumentPage = lazyRouteComponent(
  () => import('@/pages/PublicPatientDocumentPage'),
  'PublicPatientDocumentPage',
)
const StaffInvitationPage = lazyRouteComponent(() => import('@/pages/StaffInvitationPage'), 'StaffInvitationPage')
const OrganizationSelectionPage = lazyRouteComponent(() => import('@/pages/OrganizationSelectionPage'), 'OrganizationSelectionPage')
const OrganizationCreatePage = lazyRouteComponent(() => import('@/pages/OrganizationCreatePage'), 'OrganizationCreatePage')
const AccountConnectorsPage = lazyRouteComponent(() => import('@/pages/AccountConnectorsPage'), 'AccountConnectorsPage')
const AccountDesignIntegrationsPage = lazyRouteComponent(() => import('@/pages/externalIntegrations/AccountIntegrationsPage'), 'AccountDesignIntegrationsPage')
const AccountIntegrationsPage = lazyRouteComponent(() => import('@/pages/externalIntegrations/AccountIntegrationsPage'), 'AccountIntegrationsPage')
const DesignProposalsPage = lazyRouteComponent(() => import('@/pages/designProposals/DesignProposalsPage'), 'DesignProposalsPage')
const DesignProposalPage = lazyRouteComponent(() => import('@/pages/designProposals/DesignProposalPage'), 'DesignProposalPage')
const AccountDesignProposalsPage = lazyRouteComponent(() => import('@/pages/designProposals/DesignProposalsPage'), 'AccountDesignProposalsPage')
const AccountDesignProposalPage = lazyRouteComponent(() => import('@/pages/designProposals/DesignProposalPage'), 'AccountDesignProposalPage')

const { rootRoute, authenticatedRoute, publicRoutes } = createSessionRoutes({
  login: LoginPage,
  notFound: NotFoundPage,
  publicDocument: PublicPatientDocumentPage,
  invitation: StaffInvitationPage,
  passwordChange: RequiredPasswordChangePage,
  connectors: AccountConnectorsPage,
  organizations: () => <OrganizationSelectionPage resolveNotificationTarget={resolveNotificationTarget} />,
  organizationCreate: OrganizationCreatePage,
  shell: AppShell,
  integrations: AccountIntegrationsPage,
  designIntegrations: AccountDesignIntegrationsPage,
  designDrafts: AccountDesignProposalsPage,
  designDraft: AccountDesignProposalPage,
})

// The dashboard guards each contribution; entry needs only the authenticated shell.
const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: DashboardPage,
  staticData: { title: null },
})

const helpRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/help',
  component: HelpPage,
  staticData: { title: 'help.title' },
})
const helpTopicRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/help/$topicId',
  component: HelpPage,
  staticData: { title: 'help.title' },
})

// Pathless areas share guards. Unavailable routes return not-found; the API remains authoritative.
const tasksAreaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'tasks-area',
  beforeLoad: requireCapability(CAPABILITIES.tasksRead),
  staticData: { title: null },
})

const tasksRoute = createRoute({
  getParentRoute: () => tasksAreaRoute,
  path: '/tasks',
  component: TasksPage,
  // Preserve the validated schedule return day, rather than substituting today.
  validateSearch: validateTaskSearch,
  staticData: { title: WORKSPACE_DESTINATIONS.tasks.label },
})

// One guard for the list, a processing and what it proposes: the tile's own
// predicate, so a page the navigation does not offer answers not found.
const processingAreaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'processing-area',
  beforeLoad: requireProcessing,
  staticData: { title: null },
})

const processingsRoute = createRoute({
  getParentRoute: () => processingAreaRoute,
  path: '/processing',
  component: ProcessingRegistryPage,
  staticData: { title: WORKSPACE_DESTINATIONS.processing.label },
})

// Tab titles name the kind of page and never the patient it is about.
const processingRoute = createRoute({
  getParentRoute: () => processingAreaRoute,
  path: '/processing/$processingId',
  component: ProcessingPage,
  staticData: { title: 'processing.itemTitle' },
})

const processingProposalRoute = createRoute({
  getParentRoute: () => processingAreaRoute,
  path: '/processing/$processingId/proposals/$proposalId',
  component: ExternalEntryProposalPage,
  staticData: { title: 'externalEntry.aiTitle' },
})

const patientsAreaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'patients-area',
  beforeLoad: requireCapability(CAPABILITIES.patientsRead),
  staticData: { title: null },
})

const leadsAreaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'leads-area',
  beforeLoad: requireAnyCapability(LEADS_AREA_CAPABILITIES),
  staticData: { title: null },
})

const leadsRoute = createRoute({
  getParentRoute: () => leadsAreaRoute,
  path: '/leads',
  beforeLoad: requireCapability(CAPABILITIES.patientLeadsRead),
  component: LeadsPage,
  staticData: { title: WORKSPACE_DESTINATIONS.leads.label },
})

const referralPartnersRoute = createRoute({
  getParentRoute: () => leadsAreaRoute,
  path: '/leads/referral-partners',
  beforeLoad: requireCapability(CAPABILITIES.referralPartnersRead),
  component: ReferralPartnersPage,
  // The area's own section name, from the same catalogue key its tile reads.
  staticData: { title: 'lead.sectionPartners' },
})

// Finance routes and navigation use the same section admission table.
const financeAreaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'finance-area',
  beforeLoad: requireAnyCapability(FINANCE_AREA_CAPABILITIES),
  staticData: { title: null },
})

const financeIndexRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance',
  beforeLoad: enterFinance,  // A pure redirect: nothing ever renders under this path to be titled.
  staticData: { title: null },
})

const cashRegistersRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/cash-registers',
  beforeLoad: requireCapability(CAPABILITIES.financeRegistersRead),
  component: lazyRouteComponent(() => import('@/pages/finance/cashRegisters/CashRegistersPage'), 'CashRegistersPage'),
  staticData: { title: 'cashRegister.title' },
})

const cashRegisterRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/cash-registers/$registerId',
  beforeLoad: requireCapability(CAPABILITIES.financeRegistersRead),
  component: lazyRouteComponent(() => import('@/pages/finance/cashRegisters/CashRegisterPage'), 'CashRegisterPage'),
  staticData: { title: 'cashRegister.title' },
})

const cashRegisterInstallRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/cash-registers/$registerId/install',
  beforeLoad: requireCapability(CAPABILITIES.financeRegistersManage),
  component: lazyRouteComponent(() => import('@/pages/finance/cashRegisters/CashRegisterInstallationPage'), 'CashRegisterInstallationPage'),
  staticData: { title: 'cashRegister.title' },
})

const financeAccountsRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/accounts',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES.accounts),
  component: FinanceAccountsPage,
  staticData: { title: WORKSPACE_DESTINATIONS.finance.label },
})

const financeAccountRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/accounts/$accountId',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES.accounts),
  component: FinanceAccountPage,
  // The area names the tab: which account is open is what the page says.
  staticData: { title: WORKSPACE_DESTINATIONS.finance.label },
})

const personIdentityRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/persons/$personId',
  beforeLoad: requireCapability(CAPABILITIES.financeAccountsRead),
  component: lazyRouteComponent(() => import('@/pages/finance/PersonIdentityPage'), 'PersonIdentityPage'),
  staticData: { title: FINANCE_NAV_ITEMS.people.label },
})
const legalEntityIdentityRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/legal-entities/$legalEntityId',
  beforeLoad: requireCapability(CAPABILITIES.legalEntitiesRead),
  component: lazyRouteComponent(() => import('@/pages/legalEntities/LegalEntityIdentityPage'), 'LegalEntityIdentityPage'),
  staticData: { title: 'legalEntity.title' },
})

const financePeopleRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/people',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES.people),
  component: FinancePeoplePage,
  staticData: { title: FINANCE_NAV_ITEMS.people.label },
})

const financePrepaidRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/prepaid',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES.prepaid),
  component: FinancePrepaidPage,
  staticData: { title: FINANCE_NAV_ITEMS.prepaid.label },
})

const financeServicesRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/services',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES.services),
  component: FinanceServicesPage,
  staticData: { title: FINANCE_NAV_ITEMS.services.label },
})

const financePriceListsRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/price-lists',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES['price-lists']),
  component: FinancePriceListsPage,
  staticData: { title: FINANCE_NAV_ITEMS['price-lists'].label },
})

const financePriceListRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/price-lists/$listId',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES['price-lists']),
  component: FinancePriceListPage,
  // The area names the tab: which list is open is what the page says.
  staticData: { title: FINANCE_NAV_ITEMS['price-lists'].label },
})

const financeTaxesRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/taxes',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES.taxes),
  component: FinanceTaxesPage,
  staticData: { title: FINANCE_NAV_ITEMS.taxes.label },
})

const financeExternalCostsRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/external-costs',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES['external-costs']),
  component: FinanceExternalCostsPage,
  staticData: { title: FINANCE_NAV_ITEMS['external-costs'].label },
})

const financeAdministrationRoute = createRoute({
  getParentRoute: () => financeAreaRoute,
  path: '/finance/administration',
  beforeLoad: requireAnyCapability(FINANCE_SECTION_CAPABILITIES.administration),
  component: FinanceAdministrationPage,
  staticData: { title: FINANCE_NAV_ITEMS.administration.label },
})

// The addresses the price list and the external-cost catalogue had as
// workspace tiles of their own. A bookmark or a typed path from before they
// became Finance sections lands on the section; nothing renders here.
const priceListRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/price-list',
  beforeLoad: () => {
    throw redirect({ to: '/finance/price-lists', replace: true })
  },
  staticData: { title: null },
})

const externalCostsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/external-costs',
  beforeLoad: () => {
    throw redirect({ to: '/finance/external-costs', replace: true })
  },
  staticData: { title: null },
})

const patientsRoute = createRoute({
  getParentRoute: () => patientsAreaRoute,
  path: '/patients',
  component: PatientsPage,
  validateSearch: validatePatientRegistrationSearch,
  staticData: { title: WORKSPACE_DESTINATIONS.patients.label },
})

const patientOverviewRoute = createRoute({
  getParentRoute: () => patientsAreaRoute,
  path: '/patients/$patientId',
  component: PatientOverviewPage,
  // Validate origin before passing it to the page.
  validateSearch: validatePatientSearch,
  beforeLoad: ({ search }) => {
    // Previously published summary links carried the entry as a search value.
    if (search.externalEntry) throw redirect({ to: '/clinical/external-entries/$entryId', params: { entryId: search.externalEntry }, replace: true })
  },
  // Browser history and tab titles must not expose patient identity.
  staticData: { title: WORKSPACE_DESTINATIONS.patients.label },
})

// Creation moved to the chart's add panel, which saves a draft and opens it on
// its own page; an address kept from the old form lands on the chart instead.
const externalEntryNewRoute = createRoute({
  getParentRoute: () => patientsAreaRoute,
  path: '/patients/$patientId/external-entries/new',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/patients/$patientId', params: { patientId: params.patientId }, replace: true })
  },
  staticData: { title: null },
})

// The entry's page is the clinic's, like an encounter's: the old Patient-scoped
// address is kept only as a redirect so saved links and history still open it.
const externalEntryRoute = createRoute({
  getParentRoute: () => patientsAreaRoute,
  path: '/patients/$patientId/external-entries/$entryId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/clinical/external-entries/$entryId', params: { entryId: params.entryId }, replace: true })
  },
  staticData: { title: null },
})

// Module hosts resolve their own capability vocabulary and use one refusal surface.
// The route validates search shape; each module validates its closed values.
const moduleFlowRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/modules/$moduleCode',
  component: ModulePage,
  validateSearch: validateModuleWorkspaceSearch,
  // Module-specific names belong to lazy contributions.
  staticData: { title: 'app.moduleWorkspace' },
})

const moduleEventRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/modules/$moduleCode/events/$eventId',
  component: ModuleEventPage,
  validateSearch: validateModuleEventSearch,
  staticData: { title: 'app.moduleWorkspace' },
})

// The module owns its unresolved identities and link-or-register surface.
const moduleReconciliationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/modules/$moduleCode/reconciliation',
  component: ModuleReconciliationPage,
  staticData: { title: 'app.moduleWorkspace' },
})

const scheduleAreaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'schedule-area',
  beforeLoad: requireAnyCapability(SCHEDULE_AREA_CAPABILITIES),
  staticData: { title: null },
})

const scheduleRoute = createRoute({
  getParentRoute: () => scheduleAreaRoute,
  path: '/schedule',
  beforeLoad: enterSchedule,
  component: SchedulePage,
  // The page reuses this validator because non-strict useSearch bypasses it.
  validateSearch: validateScheduleSearch,
  staticData: { title: 'nav.schedule' },
})

const scheduleSectionRoute = createRoute({
  getParentRoute: () => scheduleAreaRoute,
  path: '/schedule/$section',
  beforeLoad: requireScheduleSection,
  component: SchedulePage,
  // Tab titles identify the area, not its current section.
  staticData: { title: 'nav.schedule' },
})

// Self-service profile access requires no clinic administration capability.
const profileIndexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/profile',
  beforeLoad: () => {
    throw redirect({
      to: '/profile/$section',
      params: { section: 'personal' },
      replace: true,
    })
  },
  staticData: { title: 'profile.workspaceTitle' },
})

const profileSectionRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/profile/$section',
  beforeLoad: requireProfileSection,
  component: UserProfilePage,
  validateSearch: externalAuthorizationSearch,
  staticData: { title: 'profile.workspaceTitle' },
})

// Design drafts load their own code inside the profile frame. The session's
// design access offers them; Core authorizes every read again.
const profileDesignDraftsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/profile/design-drafts',
  beforeLoad: requireDesignDrafts,
  component: DesignProposalsPage,
  staticData: { title: 'profile.workspaceTitle' },
})

const profileDesignDraftRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/profile/design-drafts/$proposalId',
  beforeLoad: requireDesignDrafts,
  component: DesignProposalPage,
  validateSearch: designProposalSearch,
  staticData: { title: 'profile.workspaceTitle' },
})

/** Control Center contains clinic-owned administrative sections only. */
const controlCenterIndexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control-center',
  beforeLoad: enterControlCenter,
  staticData: { title: WORKSPACE_DESTINATIONS['control-center'].label },
})

const controlCenterSectionRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control-center/$section',
  beforeLoad: requireControlCenterSection,
  component: ControlCenterPage,
  staticData: { title: WORKSPACE_DESTINATIONS['control-center'].label },
})

const controlCenterSectionViewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control-center/$section/$view',
  beforeLoad: requireControlCenterView,
  component: ControlCenterPage,
  staticData: { title: WORKSPACE_DESTINATIONS['control-center'].label },
})

const controlCenterUserCardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control-center/users/$accountId',
  beforeLoad: requireControlCenterUserCard,
  component: ControlCenterPage,
  staticData: { title: WORKSPACE_DESTINATIONS['control-center'].label },
})

const controlCenterModuleViewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control-center/modules/$moduleCode/$moduleView',
  beforeLoad: requireControlCenterModule,
  component: ControlCenterPage,
  staticData: { title: WORKSPACE_DESTINATIONS['control-center'].label },
})

const encounterRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clinical/encounters/$encounterId',
  beforeLoad: requireCapability(CAPABILITIES.clinicalEncounterRead),
  component: EncounterPage,
  validateSearch: validateEncounterSearch,
  staticData: { title: 'clinical.encounterTitle' },
})

const clinicalExternalEntryRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clinical/external-entries/$entryId',
  // The server reads an entry under both grants; asking for one alone would
  // open a page whose first read is refused.
  beforeLoad: async input => {
    await requireCapability(CAPABILITIES.patientsRead)(input)
    await requireCapability(CAPABILITIES.clinicalRecordsRead)(input)
  },
  component: ExternalEntryPage,
  staticData: { title: 'externalEntry.title' },
})

const clinicalRecordRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clinical/records/$recordId',
  beforeLoad: requireCapability(CAPABILITIES.clinicalRecordsRead),
  component: ClinicalRecordPage,
  staticData: { title: 'record.detailTitle' },
})

const serviceRequestRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clinical/service-requests/$requestId',
  beforeLoad: requireCapability(CAPABILITIES.serviceRequestsRead),
  component: ServiceRequestPage,
  staticData: { title: 'referral.detailTitle' },
})

const trackerReportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports',
  beforeLoad: requireTrackerReports,
  component: TrackerReportsPage,
  staticData: { title: 'tracker.reports.title' },
})

const trackerReportRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports/$number',
  beforeLoad: requireTrackerReports,
  component: TrackerReportPage,
  staticData: { title: 'tracker.report.title' },
})

const platformIndexRoute = createRoute({
  getParentRoute: () => authenticatedRoute, path: '/platform', beforeLoad: enterPlatform,
  staticData: { title: 'controlCenter.administration.platform' },
})
const platformSectionRoute = createRoute({
  getParentRoute: () => authenticatedRoute, path: '/platform/$section', beforeLoad: requirePlatformSection,
  component: ControlCenterPage, staticData: { title: 'controlCenter.administration.platform' },
})
const platformViewRoute = createRoute({
  getParentRoute: () => authenticatedRoute, path: '/platform/$section/$view', beforeLoad: requirePlatformView,
  component: ControlCenterPage, staticData: { title: 'controlCenter.administration.platform' },
})
const importsRoute = createRoute({
  getParentRoute: () => patientsAreaRoute, path: '/patients/imports', beforeLoad: requireImagingImports,
  component: ImportsPage, staticData: { title: 'controlCenter.nav.uploads' },
})
const updatesRoute = createRoute({
  getParentRoute: () => authenticatedRoute, path: '/help/updates', beforeLoad: requireCapability(CAPABILITIES.clinicSettingsRead),
  component: UpdatesPage, staticData: { title: 'controlCenter.history.title' },
})
const taskMentionsRoute = createRoute({
  getParentRoute: () => tasksAreaRoute, path: '/tasks/mentions', component: TasksPage,
  validateSearch: validateTaskSearch, staticData: { title: 'profile.nav.tasks' },
})

// No /clinical landing: only capability-guarded encounter, record and referral links.
export const routeTree = rootRoute.addChildren([
  ...publicRoutes,
  authenticatedRoute.addChildren([
    dashboardRoute,
    platformIndexRoute, platformSectionRoute, platformViewRoute, updatesRoute,
    helpRoute,
    helpTopicRoute,
    tasksAreaRoute.addChildren([tasksRoute, taskMentionsRoute]),
    processingAreaRoute.addChildren([processingsRoute, processingRoute, processingProposalRoute]),
    patientsAreaRoute.addChildren([patientsRoute, patientOverviewRoute, externalEntryNewRoute, externalEntryRoute, importsRoute]),
    moduleFlowRoute,
    moduleEventRoute,
    moduleReconciliationRoute,
    leadsAreaRoute.addChildren([leadsRoute, referralPartnersRoute]),
    personIdentityRoute,
    legalEntityIdentityRoute,
    financeAreaRoute.addChildren([
      financeIndexRoute,
      cashRegistersRoute,
      cashRegisterRoute,
      cashRegisterInstallRoute,
      financeAccountsRoute,
      financeAccountRoute,
      financePeopleRoute,
      financePrepaidRoute,
      financeServicesRoute,
      financePriceListsRoute,
      financePriceListRoute,
      financeTaxesRoute,
      financeExternalCostsRoute,
      financeAdministrationRoute,
    ]),
    priceListRoute,
    externalCostsRoute,
    scheduleAreaRoute.addChildren([scheduleRoute, scheduleSectionRoute]),
    profileIndexRoute,
    profileSectionRoute,
    profileDesignDraftsRoute,
    profileDesignDraftRoute,
    controlCenterIndexRoute,
    controlCenterSectionRoute,
    controlCenterSectionViewRoute,
    controlCenterModuleViewRoute,
    controlCenterUserCardRoute,
    encounterRoute,
    clinicalExternalEntryRoute,
    clinicalRecordRoute,
    serviceRequestRoute,
    trackerReportsRoute,
    trackerReportRoute,
  ]),
])

export interface CreateAppRouterOptions {
  queryClient: QueryClient
  /** Tests drive the router from memory; the browser uses its own history. */
  initialLocation?: string
}

export function createAppRouter({ queryClient, initialLocation }: CreateAppRouterOptions) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultNotFoundComponent: NotFoundPage,
    // Covers chunk failures below route guards. TanStack first attempts one reload;
    // if that fails, show the retry panel rather than its developer error screen.
    defaultErrorComponent: ErrorPanel,
    ...(initialLocation ? { history: createMemoryHistory({ initialEntries: [initialLocation] }) } : {}),
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}

export { safeReturnTarget }
